import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React from 'react';

import {
  Box,
  Button,
  Stack,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';

import {
  currentPageIndexGeneralSearchAtom,
  currentPageIndexSpecificSearchAtom,
  isInitializingAtom,
  locationsAtom,
  selectedLocationIdAtom,
  selectedRefinerItemsAtom,
  selectedSiteAtom,
  selectedStateAtom,
  sitesAtom,
  startRowGeneralSearchAtom,
  startRowSpecificSearchAtom,
} from '../utils/AtomConfig';
import { ComponentLocation } from '../models/ComponentLocation';

export function Locations(props: { componentLocation: ComponentLocation }) {
  const theme = useTheme();

  const navBarStyle: SxProps = {
    position: 'relative',
    backgroundColor: '#FFFFFF',
    overflowY: 'auto',
    boxShadow:
      '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
    marginTop: '10px',
    zIndex: 1,
    minHeight: '200px',
    overflowX: 'hidden',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
    marginBottom: 4,
    '& button': {
      borderRadius: 0,
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(3),
      textAlign: 'left',
      display: 'block',
      '&.active, &.active:hover': {
        backgroundColor: 'rgba(0,0,0,0.15)',
      },
      '&:not(.active):hover': {
        backgroundColor: 'rgba(0,0,0,0.08)',
      },
    },
    display: {
      xs: 'flex',
      lg: 'none',
    },
  };

  const locations = useAtomValue(locationsAtom);
  const sites = useAtomValue(sitesAtom);
  const setIsInitializing = useSetAtom(isInitializingAtom);
  const setCurrentPageIndexSpecificSearch = useSetAtom(currentPageIndexSpecificSearchAtom);
  const setStartRowSpecificSearch = useSetAtom(startRowSpecificSearchAtom);
  const setCurrentPageIndexGeneralSearch = useSetAtom(currentPageIndexGeneralSearchAtom);
  const setStartRowGeneralSearch = useSetAtom(startRowGeneralSearchAtom);
  const setSelectedSite = useSetAtom(selectedSiteAtom);
  const setSelectedRefinerItems = useSetAtom(selectedRefinerItemsAtom);
  const setSelectedState = useSetAtom(selectedStateAtom);
  const [selectedLocationId, setSelectedLocationId] = useAtom(
    selectedLocationIdAtom
  );

  function onLocationChange(id: number) {
    // Reset all data
    setIsInitializing(true);
    setCurrentPageIndexSpecificSearch(1);
    setCurrentPageIndexGeneralSearch(1);
    setStartRowSpecificSearch(0);
    setStartRowGeneralSearch(0);
    setSelectedLocationId(id);
    setSelectedRefinerItems([]);

    const selectedLocation = locations.find((location) => location.id === id);
    setSelectedState(selectedLocation?.state || 'NSW');

    // Select first site by default
    const filteredSites = sites.filter((site) => site.LocationId === id);
    if (filteredSites.length > 0) {
      const firstSite = filteredSites[0];
      setSelectedSite(firstSite);
    } else {
      setSelectedSite(null);
    }
  }

  function isLocationSelected(locationId: number) {
    return selectedLocationId === locationId;
  }

  return (
    <>
      {/* In Nav menu */}
      {props.componentLocation === 'nav' && (
        <Box sx={{ flexGrow: 1 }}>
          {locations.map((location) => (
            <Button
              key={location.id}
              variant='text'
              onClick={() => onLocationChange(location.id)}
              sx={{
                color: 'white',
                marginLeft: '20px',
                boxShadow: 'none',
                height: '62px',
                borderRadius: 0,
                backgroundColor: isLocationSelected(location.id)
                  ? 'rgba(0,0,0,0.4)'
                  : 'rgba(0,0,0,0)',
                ':hover': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                },
              }}
            >
              {location.title}
            </Button>
          ))}
        </Box>
      )}

      {/* In drawer */}
      {props.componentLocation === 'drawer' && (
        <Box
          sx={{
            backgroundColor: '#ECECF7',
            color: '#003591',
          }}
        >
          <Typography
            component='h2'
            variant='h5'
            sx={{ marginBottom: 1, color: '#003591' }}
          >
            Locations
          </Typography>
          <Stack direction='column' sx={navBarStyle}>
            {locations.map((location) => (
              <Button
                key={location.id}
                variant='text'
                className={isLocationSelected(location.id) ? 'active' : ''}
                onClick={() => onLocationChange(location.id)}
              >
                {location.title}
              </Button>
            ))}
          </Stack>
        </Box>
      )}
    </>
  );
}
